/*
 * ototo-cart.js — add to cart without leaving the page, and open the drawer.
 *
 * HOW IT WORKS
 * Every add-to-cart on the site is a real `/cart/add` form. This intercepts the
 * submit, posts it in the background, re-renders the drawer from the server and
 * opens it. With this file absent, every one of those forms still posts
 * normally and the shopper lands on the cart page — slower, but never broken.
 *
 * ⚠️ THE DRAWER MARKUP COMES FROM THE SERVER, ALWAYS.
 * After a change we re-request the current page with `?sections=` and swap the
 * drawer's HTML wholesale. Nothing here recalculates a price, a discount or a
 * free-shipping threshold — get that wrong and the drawer lies about money.
 * Shopify's own totals are the only ones shown.
 *
 * ⚠️ THE WALLET BUTTONS CANNOT BE MOVED OR CLONED.
 * Shop Pay, Apple Pay and Google Pay are rendered into the page by Shopify and
 * bound to their own iframes. Re-inserting that markup by hand detaches them
 * and they stop working — silently, and only in production. That is why the
 * drawer is re-rendered by the section-rendering API rather than patched.
 */
(function () {
  'use strict';

  var SECTION_ID = 'ototo-cart-drawer';

  function drawer() { return document.querySelector('[data-ot-drawer]'); }

  /* ------------------------------------------------------------- open/close */

  var lastFocus = null;

  function open() {
    var d = drawer();
    if (!d) return;
    lastFocus = document.activeElement;
    d.removeAttribute('hidden');
    /* Two frames: one for the attribute to land, one for the transition to
       have a starting state to animate from. */
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { d.classList.add('is-open'); });
    });
    document.documentElement.classList.add('ot-drawer-open');
    /* The scrim also carries data-ot-drawer-close and comes first in the DOM;
       focus goes to the real, named Close button. */
    var close = d.querySelector('button[data-ot-drawer-close]') || d.querySelector('[data-ot-drawer-close]:not(.ot-drawer__scrim)');
    if (close && close.focus) close.focus();
  }

  function close() {
    var d = drawer();
    if (!d) return;
    d.classList.remove('is-open');
    document.documentElement.classList.remove('ot-drawer-open');
    /* Wait for the slide-out before hiding, or it vanishes instead of leaving.
       350ms matches --ot-slow plus a frame; a transitionend listener would be
       tidier but never fires when reduced-motion collapses the duration. */
    window.setTimeout(function () { d.setAttribute('hidden', ''); }, 350);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  document.addEventListener('click', function (e) {
    if (e.target.closest && e.target.closest('[data-ot-drawer-close]')) {
      e.preventDefault();
      close();
      return;
    }
    /* Any link to /cart opens the drawer instead — the header bag included. */
    var cartLink = e.target.closest && e.target.closest('a[href$="/cart"], a[href*="/cart?"]');
    if (cartLink && drawer()) {
      e.preventDefault();
      open();
    }
  });

  document.addEventListener('keydown', function (e) {
    var d = drawer();
    if (!d || d.hasAttribute('hidden')) return;
    if (e.key === 'Escape') { close(); return; }
    /* aria-modal promises focus stays inside: Tab cycles within the drawer
       instead of walking into the page behind it (go-live 8.4). */
    if (e.key !== 'Tab') return;
    var f = [].slice.call(d.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])'))
      .filter(function (el) { return el.offsetParent !== null || el === document.activeElement; });
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (!d.contains(document.activeElement)) { e.preventDefault(); first.focus(); }
    else if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  /* ------------------------------------------------------------- rendering */

  function refresh() {
    var url = window.location.pathname + '?sections=' + SECTION_ID;
    return fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) { return r.json(); })
      .then(function (json) {
        var html = json[SECTION_ID];
        if (!html) return;
        var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-ot-drawer]');
        var current = drawer();
        if (!fresh || !current) return;
        var wasOpen = current.classList.contains('is-open');
        current.replaceWith(fresh);
        if (wasOpen) {
          fresh.removeAttribute('hidden');
          fresh.classList.add('is-open');
        }
        updateBubbles();
      })
      .catch(function () { /* leave the drawer as it was; the cart page still works */ });
  }

  function updateBubbles() {
    var count = document.querySelector('[data-ot-drawer-count]');
    if (!count) return;
    var n = count.textContent.trim();
    /* [data-ot-cart-count] is the DS header bag + the phone drawer's bag; it was
       missing here, so the header count only changed on reload (fixed 2026-10-15). */
    document.querySelectorAll('.cart-count-bubble span:first-child, [data-ot-bag-count], [data-ot-cart-count]').forEach(function (el) {
      el.textContent = n;
    });
  }

  /* ------------------------------------------------------------- adding */

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form.matches || !form.matches('form[action*="/cart/add"]')) return;

    e.preventDefault();

    var btn = form.querySelector('[type="submit"]');
    if (btn) {
      btn.classList.add('is-loading');
      btn.disabled = true;
    }

    var data = new FormData(form);
    /* Ask for the drawer back in the same round trip — one request instead of
       two, and the markup is Shopify's own. */
    data.append('sections', SECTION_ID);

    fetch('/cart/add.js', { method: 'POST', body: data, headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json().then(function (b) { return { ok: r.ok, body: b }; }); })
      .then(function (res) {
        if (!res.ok) {
          /* Sold out between page load and click is the common case here — 33
             of 146 active variants sit at or below zero stock. Say what
             happened rather than failing silently. */
          var msg = (res.body && res.body.description) || 'Sorry, that is not available.';
          var slot = form.querySelector('[data-ot-card-error]');
          if (slot) slot.textContent = msg;
          else window.alert(msg);
          return;
        }
        var sections = res.body && res.body.sections;
        if (sections && sections[SECTION_ID]) {
          var fresh = new DOMParser().parseFromString(sections[SECTION_ID], 'text/html').querySelector('[data-ot-drawer]');
          var current = drawer();
          if (fresh && current) current.replaceWith(fresh);
          updateBubbles();
        } else {
          return refresh();
        }
      })
      .then(open)
      .catch(function () {
        /* The network failed. Fall back to the real post so the shopper still
           gets their item, on the cart page. */
        form.submit();
      })
      .finally(function () {
        if (btn) {
          btn.classList.remove('is-loading');
          btn.disabled = false;
        }
      });
  });

  /* ------------------------------------------------------------- quantity */

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-ot-qty]');
    if (!btn) return;
    e.preventDefault();

    var line = btn.getAttribute('data-ot-line-index');
    var qty = btn.getAttribute('data-ot-qty');

    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: Number(line), quantity: Number(qty), sections: SECTION_ID }),
    })
      .then(function (r) { return r.json(); })
      .then(function (body) {
        var sections = body && body.sections;
        if (sections && sections[SECTION_ID]) {
          var fresh = new DOMParser().parseFromString(sections[SECTION_ID], 'text/html').querySelector('[data-ot-drawer]');
          var current = drawer();
          if (fresh && current) {
            var wasOpen = current.classList.contains('is-open');
            current.replaceWith(fresh);
            if (wasOpen) { fresh.removeAttribute('hidden'); fresh.classList.add('is-open'); }
          }
          updateBubbles();
        } else {
          return refresh();
        }
      })
      .catch(function () { window.location.href = '/cart'; });
  });

  /* An early click caught by the inline catcher in layout/theme.liquid. */
  window.__otCartReady = true;
  if (window.__otCartPending && drawer()) { window.__otCartPending = false; open(); }
})();
